import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from 'state/axios';
import toast from "react-hot-toast";
import { Formik, FieldArray } from "formik";
import * as yup from "yup";
import {
    useTheme,
    useMediaQuery,
    Box,
    Typography,
    TextField,
    Button,
    CircularProgress,
    Checkbox,
    FormControlLabel,
    IconButton,
    Autocomplete,
} from "@mui/material";
import { Add, Delete, ArrowBackIosNew } from "@mui/icons-material";
import useAuthCheck from "hooks/useAuthCheck";
import { LAYOUT_HEIGHT } from "state/constants";
import FlexBetween from "components/FlexBetween";
import FlexCenter from "components/FlexCenter";
import Header from "components/Header";
import StyledButton from 'components/StyledButton';


const TatTemplatePage = () => {
    useAuthCheck();
    const { tatTemplateId } = useParams();
    const navigate = useNavigate();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);


    const [acTypes, setACTypes] = useState([]);
    const [aircrafts, setAircrafts] = useState([]);
    const [airports, setAirports] = useState([]);
    const [airportPairs, setAirportPairs] = useState([]);

    const [formValues, setFormValues] = useState({
        acTypeCodes: [],
        acRegs: [],
        airportCodes: [],
        sectors: [],
        tasks: [],
        isActive: true,
    });

    /** API LOADERS **/
    const fetchACTypes = useCallback(async () => await api.get(
        '/actypes/active'
    ), []);

    const fetchAircrafts = useCallback(async () => await api.get(
        '/aircrafts/active'
    ), []);

    const fetchAirports = useCallback(
        async () => await api.get('/airports'),
        []
    );

    const fetchAirportPairs = useCallback(
        async () => await api.get('/airportpairs'),
        []
    );

    const fetchTatTemplate = useCallback(async () => {
        if (tatTemplateId) {
        return await api.get(`/tattemplates/${tatTemplateId}`);
        }
    }, [tatTemplateId]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                setError(null);

                const [
                    acTypesResponse,
                    aircraftsResponse,
                    airportsResponse,
                    airportPairsResponse,
                    tatTemplateResponse,
                ] = await Promise.all([
                    fetchACTypes(),
                    fetchAircrafts(),
                    fetchAirports(),
                    fetchAirportPairs(),
                    tatTemplateId ? fetchTatTemplate() : Promise.resolve(null),
                ]);

                setACTypes(acTypesResponse.data);
                setAircrafts(aircraftsResponse.data);
                setAirports(airportsResponse.data);
                const uniquePairs = Array.from(
                    new Map(airportPairsResponse.data.map((p) => [p.sector, p])).values()
                );
                setAirportPairs(uniquePairs);
                
                const data = tatTemplateResponse?.data || {};
                setFormValues({
                    acTypeCodes: data.acTypeCodes || [],
                    acRegs: data.acRegs || [],
                    airportCodes: data.airportCodes || [],
                    sectors: data.sectors || [],
                    tasks: data.tasks || [],
                    isActive: data.isActive ?? true,
                });
            } catch (err) {
                toast.error(err.response?.data?.message || "Failed to fetch data!");
                setError(err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [fetchACTypes, 
        fetchAircrafts, 
        fetchAirports, 
        fetchAirportPairs, 
        fetchTatTemplate, 
        tatTemplateId
    ]);

    /** Validation Schema **/
    const validationSchema = yup.object().shape({
        acTypeCodes: yup.array().of(yup.string()).min(1, "At least one AC Type required"),
        tasks: yup.array().of(
            yup.object().shape({
                taskCode: yup.string().required("Task code required"),
                duration: yup.number().min(0, "Must be >= 0"),
                priority: yup.number().min(0, "Must be >= 0"),
            })
        ),
    });

    /** Submit **/
    const handleFormSubmit = async (values) => {
        try {
            const method = tatTemplateId ? "PUT" : "POST";
            const url = tatTemplateId
                ? `/tattemplates/update/${tatTemplateId}`
                : '/tattemplates/new';

            await api({ method, url, data: values });

            toast.success(tatTemplateId 
                ? "Turnaround template updated." 
                : "Turnaround template created."
            );
            navigate("/tattemplates");
        } catch (err) {
            toast.error(err.response?.data?.message || "Failed to submit form!");
            setError(err);
        }
    };

    
    return (
        <Box className="main-container-box">
            <FlexBetween>
                <Header
                    title="TAT Template"
                    subtitle={loading ? "Loading..." : tatTemplateId ? "Update" : "Create"}
                />
                {!loading && (
                    <StyledButton 
                        onClick={() => navigate("/tattemplates")}
                    >
                        <ArrowBackIosNew />
                    </StyledButton>
                )}
            </FlexBetween>
            {loading ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <CircularProgress />
                </FlexCenter>
            ) : error ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <Typography color="error">Error: {error?.message || ''}</Typography>
                </FlexCenter>
            ) : (
                <Formik
                    initialValues={formValues}
                    enableReinitialize
                    validationSchema={validationSchema}
                    onSubmit={handleFormSubmit}
                >
                    {({ values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue }) => (
                        <form onSubmit={handleSubmit}>
                            <Box
                                display="grid"
                                gap={2}
                                gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                                sx={{
                                    "& > div": { gridColumn: isDesktop ? undefined : "span 12" },
                                }}
                            >
                                {/* ===== Template Header Fields ===== */}
                                <Autocomplete
                                    multiple
                                    disableCloseOnSelect
                                    options={acTypes.map((ac) => ac.acTypeCode)}
                                    value={values.acTypeCodes || []}
                                    onChange={(_, newValue) => setFieldValue("acTypeCodes", newValue)}
                                    renderInput={(params) => (
                                        <TextField {...params} label="AC Type Code" variant="filled" />
                                    )}
                                    sx={{ gridColumn: "span 6" }}
                                />

                                <Autocomplete
                                    multiple
                                    disableCloseOnSelect
                                    options={aircrafts.map((a) => a.acReg)}
                                    value={values.acRegs || []}
                                    onChange={(_, newValue) => setFieldValue("acRegs", newValue)}
                                    renderInput={(params) => (
                                        <TextField {...params} label="AC Reg" variant="filled" />
                                    )}
                                    sx={{ gridColumn: "span 6" }}
                                />

                                <Autocomplete
                                    multiple
                                    disableCloseOnSelect
                                    options={airports.map((ap) => ap.iataCode)}
                                    value={values.airportCodes || []}
                                    onChange={(_, newValue) => setFieldValue("airportCodes", newValue)}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Airport Code" variant="filled" />
                                    )}
                                    sx={{ gridColumn: "span 6" }}
                                />

                                <Autocomplete
                                    multiple
                                    disableCloseOnSelect
                                    options={airportPairs.map((pair) => pair.sector)}
                                    value={values.sectors || []}
                                    onChange={(_, newValue) => setFieldValue("sectors", newValue)}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Sector" variant="filled" />
                                    )}
                                    sx={{ gridColumn: "span 6" }}
                                />

                                    
                                <FormControlLabel
                                    label="Is Active"
                                    control={
                                        <Checkbox
                                            color="primary"
                                            name="isActive"
                                            checked={values.isActive}
                                            onChange={handleChange}
                                        />
                                    }
                                    sx={{ gridColumn: "span 6" }}
                                />

                                {/* ===== Tasks Array ===== */}
                                <Box gridColumn="span 12">
                                    <Typography variant="h6" sx={{ mb: 1 }}>
                                        Tasks
                                    </Typography>

                                    <FieldArray
                                        name="tasks"
                                        render={(arrayHelpers) => (
                                            <Box>
                                                {values.tasks?.length > 0 ? (
                                                    values.tasks.map((task, index) => (
                                                        <Box
                                                            key={index}
                                                            display="grid"
                                                            gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                                                            gap={1}
                                                            sx={{ 
                                                                mb: 1, 
                                                                p: 1, 
                                                                border: "1px solid", 
                                                                borderColor: "neutral.deep", 
                                                                borderRadius: 2 
                                                            }}
                                                        >
                                                            <TextField
                                                                variant="filled"
                                                                label="Task Code"
                                                                name={`tasks.${index}.taskCode`}
                                                                value={task.taskCode}
                                                                onChange={handleChange}
                                                                onBlur={handleBlur}
                                                                sx={{ gridColumn: { xs: "span 6", sm: "span 2" } }}
                                                            />

                                                            <TextField
                                                                variant="filled"
                                                                label="Task Name"
                                                                name={`tasks.${index}.taskName`}
                                                                value={task.taskName}
                                                                onChange={handleChange}
                                                                onBlur={handleBlur}
                                                                sx={{ gridColumn: { xs: "span 6", sm: "span 3" } }}
                                                            />

                                                            <Autocomplete
                                                                multiple
                                                                options={values.tasks
                                                                    .filter((_, i) => i !== index)
                                                                    .map((opt) => opt.taskCode || "")
                                                                    .filter((code) => code)
                                                                }
                                                                value={task.preceding || []}
                                                                onChange={(_, newValue) =>
                                                                    setFieldValue(`tasks.${index}.preceding`, newValue)
                                                                }
                                                                renderInput={(params) => (
                                                                    <TextField
                                                                        label="Preceding"
                                                                        variant="filled"
                                                                        {...params}
                                                                    />
                                                                )}
                                                                sx={{ gridColumn: { xs: "span 12", sm: "span 3" } }}
                                                            />

                                                            <TextField
                                                                variant="filled"
                                                                type="number"
                                                                label="Duration"
                                                                name={`tasks.${index}.duration`}
                                                                value={task.duration}
                                                                onChange={handleChange}
                                                                sx={{ gridColumn: { xs: "span 4", sm: "span 1" } }}
                                                            />

                                                            <TextField
                                                                variant="filled"
                                                                type="number"
                                                                label="Start Time"
                                                                name={`tasks.${index}.startTime`}
                                                                value={task.startTime}
                                                                onChange={handleChange}
                                                                sx={{ gridColumn: { xs: "span 4", sm: "span 1" } }}
                                                            />

                                                            <TextField
                                                                label="Priority"
                                                                type="number"
                                                                variant="filled"
                                                                name={`tasks.${index}.priority`}
                                                                value={task.priority}
                                                                onChange={handleChange}
                                                                sx={{ gridColumn: { xs: "span 3", sm: "span 1" } }}
                                                            />

                                                            <IconButton
                                                                color="error"
                                                                onClick={() => arrayHelpers.remove(index)}
                                                                sx={{ gridColumn: "span 1" }}
                                                            >
                                                                <Delete />
                                                            </IconButton>
                                                        </Box>
                                                    ))
                                                ) : (
                                                    <Typography>No tasks added yet.</Typography>
                                                )}

                                                <Button
                                                    startIcon={<Add />}
                                                    onClick={() =>
                                                        arrayHelpers.push({
                                                            taskCode: "",
                                                            taskName: "",
                                                            preceding: [],
                                                            duration: 0,
                                                            startTime: 0,
                                                            priority: 0,
                                                        })
                                                    }
                                                >
                                                    Add Task
                                                </Button>
                                            </Box>
                                        )}
                                    />
                                </Box>

                                <Button 
                                    type="submit" 
                                    fullWidth 
                                    variant="contained" 
                                    color="secondary" 
                                    sx={{ gridColumn: "span 12", height: 42 }}
                                >
                                    {tatTemplateId ? "Update" : "Create"}
                                </Button>
                            </Box>
                        </form>
                    )}
                </Formik>
            )}
        </Box>
    );
};

export default TatTemplatePage;
